import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Paper,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Badge,
  Grid,
  InputAdornment,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ArrowBack as BackIcon,
  Receipt as CheckIcon,
  ListAlt as ListAltIcon,
  ShoppingCart as OrdersIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PhoneNumberField as ClientPhoneInput, matchIsValidTel } from '../components/inputs/PhoneNumberField';
import LanguageSelector from '../components/inputs/LanguageSelector';
import AutocompleteField from '../components/inputs/AutocompleteField';
import { useAuth } from '../context/AuthContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { ROLES } from '../constants/roles';
import { hasPermission } from '../utils/permissions';
import { CHECK_STATUSES, DEFAULT_RESERVATION_LIMIT, DEFAULT_CURRENCY_SYMBOL } from '../constants/appConstants';
import axios from 'axios';
import ClientItemsTab from '../components/clientDetail/ClientItemsTab';
import ClientChecksTab from '../components/clientDetail/ClientChecksTab';
import ClientBalanceTab from '../components/clientDetail/ClientBalanceTab';
import ClientOrdersTab from '../components/clientDetail/ClientOrdersTab';
import ClientProfileTab from '../components/clientDetail/ClientProfileTab';
import ClientPricingFields from '../components/clientDetail/ClientPricingFields';
import { mapClientPricingToForm, buildPricingRulesPayload, DEFAULT_PRICING_RULES_FORM } from '../utils/pricingRules';
import ClientDetailHeader from '../components/clientDetail/ClientDetailHeader';
import MoveItemsToClientDialog from '../components/MoveItemsToClientDialog';
import { formatCheckListAmount, getCheckStatusColor, getCheckStatusLabel } from '../utils/checkDisplay';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useFeedback } from '../context/FeedbackContext';
import ClientTagsRow from '../components/clients/ClientTagsRow';
import ClientTagPicker from '../components/clients/ClientTagPicker';
import useCompanyClientTagDefinitions from '../hooks/useCompanyClientTagDefinitions';
import { splitClientTagsBySource, getManualClientTagIds } from '../utils/clientTags';
import { CLIENT_DETAIL_TABS, CLIENT_TAB_INDEX_BY_SLUG, CLIENT_TAB_SLUG_BY_INDEX, CLIENT_TABS_WITH_ITEM_DETAIL, buildClientListNavState, clientListNavFromState, getClientTabPath } from '../utils/navigationReturn';

const ClientDetail = () => {
  const { id: clientId, tabName, itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listNav = clientListNavFromState(location.state, clientId);
  const navIds = listNav.ids;
  const navIndex = listNav.index;
  const returnTo = listNav.returnTo;
  const hasPrev = listNav.hasPrev;
  const hasNext = listNav.hasNext;
  const activeTabSlug = tabName && CLIENT_TAB_INDEX_BY_SLUG[tabName] != null ? tabName : 'items';
  const detailTab = CLIENT_TAB_INDEX_BY_SLUG[activeTabSlug] ?? CLIENT_DETAIL_TABS.ITEMS;

  const goToClient = (targetId, index) => {
    navigate(getClientTabPath(targetId, activeTabSlug, itemId), {
      state: buildClientListNavState(navIds, index, {
        returnTo,
        returnTab: location.state?.returnTab,
      }),
    });
  };

  const goToPrev = () => {
    if (!hasPrev) return;
    goToClient(navIds[navIndex - 1], navIndex - 1);
  };

  const goToNext = () => {
    if (!hasNext) return;
    goToClient(navIds[navIndex + 1], navIndex + 1);
  };
  const { userRole, customPermissions, isInternal } = useAuth();
  const { refreshOpenOrdersCount } = useOpenOrdersCount();

  const canEditOrderItemPrice = isInternal && hasPermission(userRole, 'edit_order_item_price', customPermissions);
  const canEditOrderItemWeight = isInternal && hasPermission(userRole, 'edit_order_item_weight', customPermissions);
  const canAddCustomItem = isInternal && hasPermission(userRole, 'reserve_for_client', customPermissions);

  const [client, setClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [tagsFormIds, setTagsFormIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', vatId: '', companyName: '', city: '', country: '', address: '', reservationLimit: '', language: 'en', clientTagIds: [], pricingRules: DEFAULT_PRICING_RULES_FORM,
  });

  const { tags: companyTagDefinitions, reload: reloadCompanyTags } = useCompanyClientTagDefinitions();

  const [clientItemsTotal, setClientItemsTotal] = useState(0);

  const [clientChecks, setClientChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);

  const [moveToCheckDialogOpen, setMoveToCheckDialogOpen] = useState(false);
  const [selectedMoveCheck, setSelectedMoveCheck] = useState(null);
  const [newCheckName, setNewCheckName] = useState('');
  const [moveItemIds, setMoveItemIds] = useState([]);
  const [moveToClientDialogOpen, setMoveToClientDialogOpen] = useState(false);
  const [selectionClearSignal, setSelectionClearSignal] = useState(0);
  const { loading: saveLoading, run: runSave } = useAsyncAction();
  const { loading: tagsSaveLoading, run: runTagsSave } = useAsyncAction();
  const { loading: moveLoading, run: runMove } = useAsyncAction();

  const itemsRefreshRef = useRef(null);

  const canManage = hasPermission(userRole, 'manage_clients', customPermissions);
  const isManager = userRole === ROLES.MANAGER;
  const canManageChecks = isInternal && hasPermission(userRole, 'manage_checks', customPermissions);
  const canManageOrders = isInternal && hasPermission(userRole, 'manage_orders', customPermissions);
  const canMoveToCheck = canManageChecks;
  const canTransferItemsToClient = isInternal && (
    canManageOrders
    || canEditOrderItemPrice
    || canEditOrderItemWeight
  );
  const canChangeStatus = isInternal && hasPermission(userRole, 'change_status', customPermissions);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { showError, showSuccess } = useFeedback();

  const handleClientBack = () => {
    if (itemId && CLIENT_TABS_WITH_ITEM_DETAIL.has(activeTabSlug)) {
      navigate(getClientTabPath(clientId, activeTabSlug), { state: location.state });
      return;
    }
    navigate(returnTo || '/clients');
  };

  const detailContext = itemId && tabName === 'checks'
    ? t('cashiering.checkNumber', { id: String(itemId).slice(-6) })
    : itemId && tabName === 'orders'
      ? `${t('orders.orderNumber')}${String(itemId).slice(-6).toUpperCase()}`
      : null;

  useEffect(() => {
    if (!clientId) return;
    if (!tabName) {
      navigate(getClientTabPath(clientId, 'items'), { replace: true, state: location.state });
      return;
    }
    if (CLIENT_TAB_INDEX_BY_SLUG[tabName] == null) {
      navigate(getClientTabPath(clientId, 'items'), { replace: true, state: location.state });
      return;
    }
    if (itemId && !CLIENT_TABS_WITH_ITEM_DETAIL.has(tabName)) {
      navigate(getClientTabPath(clientId, tabName), { replace: true, state: location.state });
    }
  }, [clientId, tabName, itemId, navigate, location.state]);

  useEffect(() => {
    const fetchClient = async () => {
      setClientLoading(true);
      try {
        const res = await axios.get(`/api/users/${clientId}`);
        setClient(res.data);
      } catch (error) {
        console.error('Failed to fetch client:', error);
      } finally {
        setClientLoading(false);
      }
    };
    fetchClient();
  }, [clientId]);

  const refreshClient = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`/api/users/${clientId}`);
      setClient(res.data);
    } catch (error) {
      console.error('Failed to refresh client:', error);
    }
  }, [clientId]);

  const fetchClientChecks = useCallback(async (cId) => {
    if (!canManageChecks) {
      setClientChecks([]);
      return;
    }
    setChecksLoading(true);
    try {
      const res = await axios.get('/api/checks', { params: { clientUser: cId } });
      setClientChecks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch checks:', error);
      setClientChecks([]);
    } finally {
      setChecksLoading(false);
    }
  }, [canManageChecks]);

  const handleDeleteClient = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/users/${clientId}`);
      navigate('/clients');
    } catch (error) {
      showError(error.response?.data?.error || t('clients.deleteFailed'));
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveClient = async () => {
    const phoneTrim = (editFormData.phone || '').trim();
    if (phoneTrim && !matchIsValidTel(phoneTrim)) {
      showError(t('clients.invalidPhone'));
      return;
    }
    if (isManager) {
      const reservationLimitVal = editFormData.reservationLimit === ''
        ? null
        : parseFloat(editFormData.reservationLimit);
      if (editFormData.reservationLimit !== '' && (isNaN(reservationLimitVal) || reservationLimitVal < 0)) {
        showError(t('clients.reservationLimitInvalid'));
        return;
      }
    }
    await runSave(async () => {
      try {
        const payload = {
          firstName: editFormData.firstName,
          lastName: editFormData.lastName,
          email: editFormData.email,
          phone: phoneTrim || undefined,
          vatId: editFormData.vatId,
          companyName: editFormData.companyName,
          city: editFormData.city,
          country: editFormData.country,
          address: editFormData.address,
          language: editFormData.language,
        };
        if (isManager) {
          const reservationLimitVal = editFormData.reservationLimit === ''
            ? null
            : parseFloat(editFormData.reservationLimit);
          payload.reservationLimit = reservationLimitVal;
        }
        if (canManage) {
          payload.clientTagIds = editFormData.clientTagIds || [];
        }
        if (isManager) {
          Object.assign(payload, buildPricingRulesPayload(editFormData));
        }
        await axios.put(`/api/users/${clientId}`, payload);
        setEditDialogOpen(false);
        const res = await axios.get(`/api/users/${clientId}`);
        setClient(res.data);
      } catch (error) {
        showError(error.response?.data?.error || t('clients.updateFailed'));
      }
    });
  };

  const handleOpenTagsDialog = async () => {
    await reloadCompanyTags();
    setTagsFormIds(getManualClientTagIds(client));
    setTagsDialogOpen(true);
  };

  const handleSaveTags = async () => {
    await runTagsSave(async () => {
      try {
        await axios.put(`/api/users/${clientId}`, { clientTagIds: tagsFormIds });
        setTagsDialogOpen(false);
        await refreshClient();
      } catch (error) {
        showError(error.response?.data?.error || t('clients.tags.saveFailed'));
      }
    });
  };

  const handleDetailTabChange = (event, newValue) => {
    const slug = CLIENT_TAB_SLUG_BY_INDEX[newValue] || 'items';
    navigate(getClientTabPath(clientId, slug), { state: location.state });
  };

  const handleOpenMoveToCheck = (itemIds) => {
    setMoveItemIds(itemIds);
    fetchClientChecks(clientId);
    setMoveToCheckDialogOpen(true);
  };

  const handleOpenMoveToClient = (itemIds) => {
    setMoveItemIds(itemIds);
    setMoveToClientDialogOpen(true);
  };

  const handleMoveItemsToClientSuccess = async (data) => {
    const ref = data?.order?._id?.slice(-6)?.toUpperCase() || '';
    showSuccess(t('clients.moveToClientSuccess', {
      count: data?.movedCount || moveItemIds.length,
      name: data?.toClientName || getClientName(data?.order?.clientUser) || '—',
      ref,
    }));
    setMoveItemIds([]);
    setSelectionClearSignal((x) => x + 1);
    await itemsRefreshRef.current?.();
    await refreshClient();
  };

  const handleMoveItemsToCheck = async (checkId) => {
    if (moveItemIds.length === 0) return;
    await runMove(async () => {
      try {
        await axios.post(`/api/checks/${checkId}/items`, {
          itemIds: moveItemIds,
        });
        setMoveItemIds([]);
        setMoveToCheckDialogOpen(false);
        setSelectedMoveCheck(null);
        setNewCheckName('');
        setSelectionClearSignal((x) => x + 1);
        await itemsRefreshRef.current?.();
        await fetchClientChecks(clientId);
      } catch (error) {
        showError(error.response?.data?.error || t('clients.moveToCheckFailed'));
      }
    });
  };

  const handleMoveDialogCreateCheck = async (name, andMoveItems = false) => {
    await runMove(async () => {
      try {
        const res = await axios.post('/api/checks', {
          clientUser: clientId,
          name: name || undefined,
        });
        const createdCheck = res.data;
        if (andMoveItems && moveItemIds.length > 0) {
          await axios.post(`/api/checks/${createdCheck._id}/items`, {
            itemIds: moveItemIds,
          });
          setMoveItemIds([]);
          setSelectionClearSignal((x) => x + 1);
          await itemsRefreshRef.current?.();
        }
        setMoveToCheckDialogOpen(false);
        setSelectedMoveCheck(null);
        setNewCheckName('');
        await fetchClientChecks(clientId);
        await refreshClient();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToCreateCheck'));
      }
    });
  };

  const getClientName = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';

  const handleOpenEditDialog = () => {
    if (!client) return;
    setEditFormData({
      firstName: client.firstName || '',
      lastName: client.lastName || '',
      email: client.email || '',
      phone: client.phone || '',
      vatId: client.vatId || '',
      companyName: client.companyName || '',
      city: client.city || '',
      country: client.country || '',
      address: client.address || '',
      reservationLimit: client.reservationLimit != null ? String(client.reservationLimit) : '',
      language: client.language || 'en',
      clientTagIds: getManualClientTagIds(client),
      ...mapClientPricingToForm(client),
    });
    setEditDialogOpen(true);
  };

  const handleCopyInvoiceId = async (invoiceId) => {
    try {
      await navigator.clipboard.writeText(invoiceId);
      showSuccess(t('clients.profile.copied'));
    } catch {
      showError(t('common.error'));
    }
  };

  if (clientLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Typography color="error" variant="h6">{t('clients.clientNotFound')}</Typography>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/clients')} sx={{ mt: 2 }}>
            {t('clients.backToClients')}
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" disableGutters>
        <ClientDetailHeader
          client={client}
          clientName={getClientName(client)}
          isMobile={isMobile}
          navIds={navIds}
          navIndex={navIndex}
          onBack={handleClientBack}
          onPrev={goToPrev}
          onNext={goToNext}
          clientItemsTotal={clientItemsTotal}
          detailContext={detailContext}
        />

        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={detailTab}
            onChange={handleDetailTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{ borderBottom: 1, borderColor: 'divider', ...(isMobile ? {} : { px: 2 }) }}
          >
            <Tab
              icon={(
                <Badge badgeContent={clientItemsTotal} color="primary" max={999} invisible={clientItemsTotal === 0}>
                  <ListAltIcon fontSize="small" />
                </Badge>
              )}
              iconPosition="start"
              label={t('clients.items')}
            />
            <Tab
              icon={(
                <Badge badgeContent={client?.openChecksCount ?? 0} color="warning" max={99} invisible={(client?.openChecksCount ?? 0) === 0}>
                  <CheckIcon fontSize="small" />
                </Badge>
              )}
              iconPosition="start"
              label={t('clients.checks')}
            />
            <Tab label={t('clients.orders')} icon={<OrdersIcon fontSize="small" />} iconPosition="start" />
            <Tab label={t('clients.balance')} icon={<WalletIcon fontSize="small" />} iconPosition="start" />
            <Tab label={t('clients.profile.tab')} icon={<PersonIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Paper>

        <Box sx={{ display: detailTab === CLIENT_DETAIL_TABS.ITEMS ? 'block' : 'none' }}>
          <ClientItemsTab
            clientId={clientId}
            active={detailTab === CLIENT_DETAIL_TABS.ITEMS}
            isMobile={isMobile}
            isManager={isManager}
            canEditOrderItemPrice={canEditOrderItemPrice}
            canEditOrderItemWeight={canEditOrderItemWeight}
            canAddCustomItem={canAddCustomItem}
            canMoveToCheck={canMoveToCheck}
            canTransferItemsToClient={canTransferItemsToClient}
            canManageOrders={canManageOrders}
            canChangeStatus={canChangeStatus}
            onItemsTotalChange={setClientItemsTotal}
            onOpenMoveToCheck={handleOpenMoveToCheck}
            onOpenMoveToClient={handleOpenMoveToClient}
            refreshOpenOrdersCount={refreshOpenOrdersCount}
            fetchClientChecks={fetchClientChecks}
            refreshClient={refreshClient}
            selectionClearSignal={selectionClearSignal}
            itemsRefreshRef={itemsRefreshRef}
          />
        </Box>

        <Box sx={{ display: detailTab === CLIENT_DETAIL_TABS.CHECKS ? 'block' : 'none' }}>
          <ClientChecksTab
            clientId={clientId}
            client={client}
            active={detailTab === CLIENT_DETAIL_TABS.CHECKS}
            itemId={tabName === 'checks' ? itemId : undefined}
            isMobile={isMobile}
            clientChecks={clientChecks}
            checksLoading={checksLoading}
            fetchClientChecks={fetchClientChecks}
            onChecksUpdated={refreshClient}
          />
        </Box>

        <Box sx={{ display: detailTab === CLIENT_DETAIL_TABS.ORDERS ? 'block' : 'none' }}>
          <ClientOrdersTab
            clientId={clientId}
            active={detailTab === CLIENT_DETAIL_TABS.ORDERS}
            itemId={tabName === 'orders' ? itemId : undefined}
          />
        </Box>

        <Box sx={{ display: detailTab === CLIENT_DETAIL_TABS.BALANCE ? 'block' : 'none' }}>
          <ClientBalanceTab
            clientId={clientId}
            active={detailTab === CLIENT_DETAIL_TABS.BALANCE}
            isMobile={isMobile}
            isManager={isManager}
          />
        </Box>

        <Box sx={{ display: detailTab === CLIENT_DETAIL_TABS.PROFILE ? 'block' : 'none' }}>
          <ClientProfileTab
            client={client}
            isManager={isManager}
            canManage={canManage}
            onEdit={handleOpenEditDialog}
            onManageTags={handleOpenTagsDialog}
            onDelete={() => setDeleteDialogOpen(true)}
            onCopyInvoiceId={handleCopyInvoiceId}
          />
        </Box>

        <Dialog open={moveToCheckDialogOpen} onClose={() => { setMoveToCheckDialogOpen(false); setSelectedMoveCheck(null); setNewCheckName(''); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Move {moveItemIds.length} item{moveItemIds.length !== 1 ? 's' : ''} to Check</DialogTitle>
          <DialogContent dividers>
            {checksLoading ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
            ) : (
              <Box sx={{ pt: 1 }}>
                <AutocompleteField
                  value={selectedMoveCheck?._id || ''}
                  onChange={(nextId) => {
                    const val = ([
                      { _id: '__new__', name: '+ Create New Check', status: 'new' },
                      ...clientChecks.filter((c) => c.status !== CHECK_STATUSES.CLOSED),
                    ]).find((o) => o._id === nextId) || null;

                    if (val && val._id === '__new__') {
                      setSelectedMoveCheck(val);
                    } else {
                      setSelectedMoveCheck(val);
                      setNewCheckName('');
                    }
                  }}
                  options={[
                    { _id: '__new__', name: '+ Create New Check', status: 'new' },
                    ...clientChecks.filter((c) => c.status !== CHECK_STATUSES.CLOSED),
                  ]}
                  label="Select Check"
                  placeholder="Choose existing or create new"
                  getOptionValue={(opt) => opt._id}
                  getOptionLabel={(opt) => {
                    if (opt._id === '__new__') return '+ Create New Check';
                    return opt.name || `Check #${opt._id.slice(-6)}`;
                  }}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt._id}>
                      {opt._id === '__new__' ? (
                        <Typography color="primary" fontWeight="bold">+ Create New Check</Typography>
                      ) : (
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{opt.name || `Check #${opt._id.slice(-6)}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.itemCount || 0} items | Due: {formatCheckListAmount(opt.amountDue, opt)}
                            </Typography>
                          </Box>
                          <Chip label={getCheckStatusLabel(opt.status, t)} color={getCheckStatusColor(opt.status)} size="small" sx={{ ml: 1 }} />
                        </Box>
                      )}
                    </li>
                  )}
                  fullWidth
                />
                {selectedMoveCheck?._id === '__new__' && (
                  <TextField
                    fullWidth label="New Check Name (optional)" value={newCheckName}
                    onChange={(e) => setNewCheckName(e.target.value)}
                    placeholder="e.g. Invoice #123" margin="normal" size="small"
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setMoveToCheckDialogOpen(false); setSelectedMoveCheck(null); setNewCheckName(''); }} disabled={moveLoading}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!selectedMoveCheck || moveLoading}
              onClick={() => {
                if (selectedMoveCheck._id === '__new__') {
                  handleMoveDialogCreateCheck(newCheckName, true);
                } else {
                  handleMoveItemsToCheck(selectedMoveCheck._id);
                }
              }}
            >
              {moveLoading
                ? t('common.saving')
                : (selectedMoveCheck?._id === '__new__' ? 'Create & Move' : 'Move to Check')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          scroll="paper"
          PaperProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: isMobile ? '100%' : '90dvh',
            },
          }}
        >
          <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0 }}>{t('clients.editClient')}</DialogTitle>
          <DialogContent dividers sx={{ px: 3, pt: 3, pb: 3, flex: '1 1 auto', overflowY: 'auto' }}>
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.firstName')} value={editFormData.firstName}
                  onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.lastName')} value={editFormData.lastName}
                  onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.email')} type="email" value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ClientPhoneInput
                  label={t('clients.phoneOptional')}
                  value={editFormData.phone}
                  onChange={(value) => setEditFormData({ ...editFormData, phone: value })}
                  helperText={t('clients.phoneHelper')}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.vatId')} value={editFormData.vatId}
                  onChange={(e) => setEditFormData({ ...editFormData, vatId: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.companyName')} value={editFormData.companyName}
                  onChange={(e) => setEditFormData({ ...editFormData, companyName: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.city')} value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.country')} value={editFormData.country}
                  onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label={t('clients.address')} value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ pt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {t('clients.language')}
                  </Typography>
                  <LanguageSelector
                    value={editFormData.language}
                    onChange={(value) => setEditFormData({ ...editFormData, language: value })}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {t('clients.languageHelp')}
                  </Typography>
                </Box>
              </Grid>
              {canManage && (
                <Grid item xs={12}>
                  <ClientTagPicker
                    tags={companyTagDefinitions}
                    value={editFormData.clientTagIds}
                    onChange={(next) => setEditFormData({ ...editFormData, clientTagIds: next })}
                    label={t('clients.tags.manual')}
                    showSettingsLink
                    isManager={isManager}
                  />
                  {(() => {
                    const { rule } = splitClientTagsBySource(client?.clientTags || []);
                    if (rule.length === 0) return null;
                    return (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {t('clients.tags.alsoAppliedByRules')}
                        </Typography>
                        <ClientTagsRow tags={rule} />
                      </Box>
                    );
                  })()}
                </Grid>
              )}
              {isManager && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('clients.pricing.section')}
                  </Typography>
                  <ClientPricingFields
                    pricingRules={editFormData.pricingRules}
                    onChange={(pricingRules) => setEditFormData({ ...editFormData, pricingRules })}
                    currencySymbol={DEFAULT_CURRENCY_SYMBOL}
                  />
                </Grid>
              )}
              {isManager && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('clients.reservationLimit')}
                    value={editFormData.reservationLimit}
                    onChange={(e) => setEditFormData({ ...editFormData, reservationLimit: e.target.value })}
                    type="number"
                    inputProps={{ min: 0, step: 100 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    helperText={t('clients.reservationLimitHelp', { currency: DEFAULT_CURRENCY_SYMBOL, amount: DEFAULT_RESERVATION_LIMIT.toLocaleString() })}
                    margin="none"
                    placeholder={t('clients.reservationLimitPlaceholder', { amount: DEFAULT_RESERVATION_LIMIT.toLocaleString() })}
                  />
                </Grid>
              )}
              {client.invoiceExternalId && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth label={t('clients.invoiceExternalId')} value={client.invoiceExternalId}
                    margin="none"
                    disabled
                    helperText={t('clients.invoiceExternalIdHelper')}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 2 }}>
            <Button onClick={() => setEditDialogOpen(false)} disabled={saveLoading}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveClient} variant="contained" disabled={saveLoading}>
              {saveLoading ? t('common.saving') : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={tagsDialogOpen}
          onClose={() => !tagsSaveLoading && setTagsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('clients.tags.manageTitle')}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <ClientTagPicker
              tags={companyTagDefinitions}
              value={tagsFormIds}
              onChange={setTagsFormIds}
              label={t('clients.tags.manual')}
              showSettingsLink
              isManager={isManager}
            />
            {(() => {
              const { rule } = splitClientTagsBySource(client?.clientTags || []);
              if (rule.length === 0) return null;
              return (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('clients.tags.alsoAppliedByRules')}
                  </Typography>
                  <ClientTagsRow tags={rule} />
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTagsDialogOpen(false)} disabled={tagsSaveLoading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveTags} variant="contained" disabled={tagsSaveLoading}>
              {tagsSaveLoading ? t('common.saving') : t('clients.tags.saveTags')}
            </Button>
          </DialogActions>
        </Dialog>

        <MoveItemsToClientDialog
          open={moveToClientDialogOpen}
          onClose={() => setMoveToClientDialogOpen(false)}
          itemIds={moveItemIds}
          currentClientId={clientId}
          onSuccess={handleMoveItemsToClientSuccess}
          isMobile={isMobile}
        />

        <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
          <DialogTitle>{t('clients.deleteClient')}</DialogTitle>
          <DialogContent>
            <Typography>{t('clients.deleteConfirm', { name: getClientName(client) })}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>{t('common.cancel')}</Button>
            <Button onClick={handleDeleteClient} color="error" variant="contained" disabled={deleteLoading}>
              {t('clients.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ClientDetail;
